// src/components/pages/RRHHHome.jsx
// Página de Inicio del módulo RRHH / Control de Personal
// Enfoque principal: Cumpleaños de asesores + accesos rápidos + resumen del día
import React, { useState, useMemo } from 'react';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../services/firebase';
import {
  FiGift, FiCalendar, FiUsers, FiArrowRight, FiEdit2, FiCheck,
  FiHeart, FiHome, FiUserPlus, FiClock, FiFileText, FiStar, FiAward, FiBriefcase
} from 'react-icons/fi';
import { format, differenceInDays, parseISO, isValid } from 'date-fns';
import { es } from 'date-fns/locale';
import toast from 'react-hot-toast';

const MONTHS_ES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

const Avatar = ({ u, size = 14 }) => {
  const initials = (u?.displayName || u?.email || '?').charAt(0).toUpperCase();
  const color = u?.asesorConfig?.color || '#0ea5e9';
  return u?.photoURL ? (
    <img src={u.photoURL} alt="" className={`w-${size} h-${size} rounded-2xl object-cover ring-2 ring-white shadow-sm shrink-0`} />
  ) : (
    <div
      className={`w-${size} h-${size} rounded-2xl flex items-center justify-center text-white font-extrabold text-xl shrink-0 shadow-sm`}
      style={{ backgroundColor: color }}
    >
      {initials}
    </div>
  );
};

const getBirthDate = (u) => {
  const b = u?.birthDate;
  if (!b) return null;
  try {
    if (typeof b === 'string') {
      // support 'YYYY-MM-DD' or full ISO
      return b.length === 10 ? parseISO(b) : new Date(b);
    }
    if (b?.toDate) return b.toDate(); // Firestore Timestamp
    return new Date(b);
  } catch { return null; }
};

const getNextBirthdayInfo = (birthDate) => {
  if (!birthDate || !isValid(birthDate)) return null;
  const today = new Date();
  const thisYear = today.getFullYear();

  let next = new Date(thisYear, birthDate.getMonth(), birthDate.getDate());
  if (next < today) {
    next = new Date(thisYear + 1, birthDate.getMonth(), birthDate.getDate());
  }

  const daysUntil = Math.max(0, differenceInDays(next, today));
  const ageTurning = next.getFullYear() - birthDate.getFullYear();

  return {
    date: next,
    daysUntil,
    ageTurning,
    month: MONTHS_ES[birthDate.getMonth()],
    day: birthDate.getDate(),
    isToday: daysUntil === 0
  };
};

const formatBirthday = (info) => {
  if (!info) return '';
  return `${info.day} de ${info.month}`;
};


const RRHHHome = ({ users = [], isAdmin, currentUser, attendanceRecords = [], onSetViewAs, onOpenDocs, onOpenControl, onOpenSettings }) => {
  const [search, setSearch] = useState('');

  const asesores = useMemo(() => {
    return users
      .filter(u => u.isAsesor === true && (u.status || 'active') !== 'inactive')
      .sort((a, b) => (a.displayName || '').localeCompare(b.displayName || ''));
  }, [users]);

  const filteredAsesores = useMemo(() => {
    if (!search) return asesores;
    const q = search.toLowerCase();
    return asesores.filter(u =>
      (u.displayName || '').toLowerCase().includes(q) ||
      (u.email || '').toLowerCase().includes(q)
    );
  }, [asesores, search]);

  // Compute birthdays with next occurrence info
  const birthdayList = useMemo(() => {
    return asesores
      .map(u => {
        const info = getNextBirthdayInfo(getBirthDate(u));
        return { user: u, info };
      })
      .filter(item => item.info) // only those with date for sorting
      .sort((a, b) => a.info.daysUntil - b.info.daysUntil);
  }, [asesores]);

  const upcoming = birthdayList.slice(0, 8); // show first 8 soonest
  const todayBirthdays = birthdayList.filter(b => b.info.isToday);
  const missingCount = asesores.length - birthdayList.length;

  const handleViewAs = (userId) => {
    if (onSetViewAs) onSetViewAs(userId);
  };

  // Simple today stats (asesores that have active entry today)
  const todayStr = format(new Date(), 'yyyy-MM-dd');
  const asesoresWorking = useMemo(() => {
    const workingIds = new Set(
      attendanceRecords
        .filter(r => r.date === todayStr && r.entryTime && !r.exitTime)
        .map(r => r.userId)
    );
    return asesores.filter(a => workingIds.has(a.id)).length;
  }, [attendanceRecords, asesores, todayStr]);

  const totalAsesores = asesores.length;

  // Work anniversaries and recent hires helpers
  const getHireDate = (u) => {
    const h = u?.hireDate || u?.fechaIngreso || u?.createdAt;
    if (!h) return null;
    try {
      if (typeof h === 'string') return h.length === 10 ? parseISO(h) : new Date(h);
      if (h?.toDate) return h.toDate();
      return new Date(h);
    } catch { return null; }
  };

  const getAnniversaryInfo = (hireDate) => {
    if (!hireDate || !isValid(hireDate)) return null;
    const today = new Date();
    const thisYear = today.getFullYear();
    let next = new Date(thisYear, hireDate.getMonth(), hireDate.getDate());
    if (next < today) next = new Date(thisYear + 1, hireDate.getMonth(), hireDate.getDate());
    const years = next.getFullYear() - hireDate.getFullYear();
    const daysUntil = Math.max(0, differenceInDays(next, today));
    return { years, daysUntil, isToday: daysUntil === 0, month: MONTHS_ES[hireDate.getMonth()], day: hireDate.getDate() };
  };

  const recentHires = useMemo(() => {
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - 60);
    return users
      .filter(u => (u.status || 'active') !== 'inactive')
      .map(u => {
        const created = getHireDate(u);
        if (!created) return null;
        return { user: u, created, daysAgo: Math.max(0, differenceInDays(new Date(), created)) };
      })
      .filter(Boolean)
      .filter(item => item.created >= cutoff)
      .sort((a, b) => b.created - a.created)
      .slice(0, 6);
  }, [users]);

  // Combine birthdays + anniversaries for Celebraciones
  const celebrations = useMemo(() => {
    const list = [];
    asesores.forEach(u => {
      const b = getBirthDate(u);
      const bInfo = getNextBirthdayInfo(b);
      if (bInfo) list.push({ type: 'birthday', user: u, info: bInfo });

      const h = getHireDate(u);
      const aInfo = getAnniversaryInfo(h);
      if (aInfo && aInfo.years > 0) {
        list.push({ type: 'anniversary', user: u, info: aInfo });
      }
    });
    return list.sort((a, b) => (a.info.daysUntil || 999) - (b.info.daysUntil || 999)).slice(0, 12);
  }, [asesores]);

  return (
    <div className="h-full overflow-y-auto bg-gray-50">
      <div className="max-w-[1400px] mx-auto p-6 lg:p-10 xl:p-12">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6">
          <div>
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-sky-600 to-blue-700 flex items-center justify-center text-white shadow-inner">
                <FiHome size={22} />
              </div>
              <div>
                <h1 className="text-3xl font-extrabold tracking-tight text-gray-900">Inicio</h1>
                <p className="text-sm text-gray-500 font-medium">
                  {format(new Date(), "EEEE, d 'de' MMMM yyyy", { locale: es })}
                </p>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Buscar asesor..."
              className="w-64 text-sm border border-gray-200 bg-white rounded-2xl px-4 py-2 focus:outline-none focus:ring-2 focus:ring-sky-300"
            />
            {isAdmin && (
              <button onClick={onOpenDocs} className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 hover:border-gray-300 rounded-2xl text-sm font-semibold text-gray-700">
                <FiUsers size={16} /> Recursos Humanos
              </button>
            )}
          </div>
        </div>

        {/* Celebration banner if anyone has birthday TODAY */}
        {todayBirthdays.length > 0 && (
          <div className="mb-6 rounded-3xl bg-gradient-to-r from-amber-400 via-orange-400 to-yellow-400 p-5 text-white shadow-lg flex items-center gap-4">
            <FiGift size={42} className="shrink-0" />
            <div className="flex-1">
              <div className="font-extrabold text-xl">¡Feliz cumpleaños hoy!</div>
              <div className="text-white/90 text-sm mt-0.5">
                {todayBirthdays.map((b, i) => (
                  <span key={i}>{b.user.displayName || b.user.email?.split('@')[0]}{i < todayBirthdays.length - 1 ? ', ' : ''}</span>
                ))}
              </div>
            </div>
            <button onClick={() => { /* future: bulk congratulate */ toast('¡Qué viva la fiesta!') }} className="px-5 py-2 bg-white/20 hover:bg-white/30 rounded-2xl text-sm font-bold backdrop-blur">
              Celebrar
            </button>
          </div>
        )}

        {/* Quick stats row - professional dashboard style */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-8">
          <div className="bg-white rounded-3xl p-4 border border-gray-100">
            <div className="text-xs uppercase tracking-widest text-gray-500 font-bold mb-1">Equipo activo</div>
            <div className="text-4xl font-extrabold tabular-nums text-gray-900">{totalAsesores}</div>
            <div className="text-[11px] text-gray-500">asesores</div>
          </div>
          <div className="bg-white rounded-3xl p-4 border border-gray-100">
            <div className="text-xs uppercase tracking-widest text-gray-500 font-bold mb-1">En jornada ahora</div>
            <div className="text-4xl font-extrabold tabular-nums text-emerald-600">{asesoresWorking}</div>
            <div className="text-[11px] text-emerald-600">trabajando</div>
          </div>
          <div className="bg-white rounded-3xl p-4 border border-gray-100">
            <div className="text-xs uppercase tracking-widest text-gray-500 font-bold mb-1">Celebraciones</div>
            <div className="text-4xl font-extrabold tabular-nums text-amber-600">{todayBirthdays.length + upcoming.length}</div>
            <div className="text-[11px] text-gray-500">próximas / hoy</div>
          </div>
          <div className="bg-white rounded-3xl p-4 border border-gray-100">
            <div className="text-xs uppercase tracking-widest text-gray-500 font-bold mb-1">Nuevos este mes</div>
            <div className="text-4xl font-extrabold tabular-nums text-sky-600">{recentHires.length}</div>
            <div className="text-[11px] text-gray-500">en los últimos 60 días</div>
          </div>
          <div onClick={onOpenDocs} className="bg-white hover:bg-rose-50 active:bg-rose-100 cursor-pointer transition rounded-3xl p-4 border border-gray-100 flex flex-col justify-between">
            <div>
              <div className="text-xs uppercase tracking-widest text-gray-500 font-bold mb-1">Documentación</div>
              <div className="text-4xl font-extrabold tabular-nums text-rose-600">{missingCount}</div>
              <div className="text-[11px] text-rose-600">pendientes de completar</div>
            </div>
          </div>
          <div onClick={onOpenControl} className="bg-white hover:bg-sky-50 active:bg-sky-100 cursor-pointer transition rounded-3xl p-4 border border-gray-100 flex flex-col justify-between">
            <div>
              <div className="text-xs uppercase tracking-widest text-gray-500 font-bold mb-1">Solicitudes</div>
              <div className="text-4xl font-extrabold tabular-nums text-violet-600">4</div>
              <div className="text-[11px] text-violet-600">de tiempo pendientes</div>
            </div>
            <div className="text-sky-600 flex items-center gap-1 text-sm font-bold mt-1">Revisar <FiArrowRight /></div>
          </div>
        </div>

        {/* === CELEBRACIONES (Cumpleaños + Aniversarios) - like professional dashboards === */}
        <div className="mb-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <FiGift className="text-rose-500" size={22} />
            <div>
              <span className="font-extrabold text-2xl text-gray-900">Celebraciones</span>
              <span className="ml-3 text-sm font-semibold text-gray-500">{celebrations.length} eventos próximos</span>
            </div>
          </div>
          {missingCount > 0 && isAdmin && (
            <div className="text-xs bg-amber-100 text-amber-700 px-3 py-1 rounded-full font-bold">
              {missingCount} sin fecha de nacimiento
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-4 mb-8">
          {celebrations.length > 0 ? celebrations.map((item, idx) => {
            const { user, info, type } = item;
            const isToday = info.isToday;
            const isAnniv = type === 'anniversary';
            return (
              <div key={idx} className={`group bg-white rounded-3xl border shadow-sm p-4 flex flex-col transition-all hover:shadow-md ${isToday ? 'border-amber-300 bg-amber-50/30 ring-1 ring-amber-200' : 'border-gray-100'}`}>
                <div className="flex items-start gap-3">
                  <div className="relative">
                    <Avatar u={user} size={12} />
                    {isToday && <div className="absolute -top-1 -right-1 bg-amber-500 text-white text-[10px] font-bold px-1.5 py-px rounded-full shadow">¡HOY!</div>}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-extrabold text-gray-900 text-[14.5px] truncate">{user.displayName || user.email?.split('@')[0]}</div>
                    <div className="text-[11px] text-gray-500">{user.email}</div>

                    <div className="mt-2 flex items-center gap-2">
                      {isAnniv ? <FiAward className="text-violet-500" size={15} /> : <FiGift className="text-sky-600" size={15} />}
                      <span className={`text-sm font-bold ${isToday ? 'text-amber-700' : 'text-gray-800'}`}>
                        {isAnniv ? `${info.years} años en el equipo` : formatBirthday(info)}
                      </span>
                    </div>
                    <div className="text-xs mt-0.5">
                      {isToday ? (
                        <span className="font-extrabold text-amber-600">{isAnniv ? '¡Feliz aniversario!' : '¡Feliz cumpleaños!'}</span>
                      ) : info.daysUntil <= 7 ? (
                        <span className="font-semibold text-emerald-600">En {info.daysUntil} día{info.daysUntil !== 1 ? 's' : ''}</span>
                      ) : (
                        <span className="text-gray-500">Faltan {info.daysUntil} días</span>
                      )}
                    </div>
                  </div>
                </div>
                {isAdmin && (
                  <div className="mt-auto pt-3 flex gap-2">
                    <button onClick={() => handleViewAs(user.id)} className="flex-1 text-xs font-bold px-3 py-1.5 rounded-2xl bg-gray-900 hover:bg-black text-white">Ver panel</button>
                    <button onClick={() => toast.success('Próximamente: saludo rápido')} className="text-xs font-semibold px-3 py-1.5 rounded-2xl border border-gray-200 hover:bg-gray-50 text-gray-600 flex items-center gap-1">
                      <FiHeart size={12} />
                    </button>
                  </div>
                )}
              </div>
            );
          }) : (
            <div className="col-span-full bg-white border border-gray-100 rounded-3xl p-8 text-center">
              <FiCalendar className="mx-auto mb-2 text-gray-300" size={36} />
              <p className="font-semibold text-gray-600">Aún no hay celebraciones cargadas</p>
              <p className="text-sm text-gray-400 mt-1">Agrega fechas de nacimiento en las tarjetas o asegúrate de tener fecha de ingreso en los usuarios.</p>
            </div>
          )}
        </div>

        {/* === NUEVOS EMPLEADOS + EQUIPO HOY (two rich columns) === */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Nuevos Empleados */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <FiUserPlus className="text-emerald-600" size={18} />
              <span className="font-extrabold text-xl text-gray-900">Nuevos en el equipo</span>
              <span className="text-sm text-gray-500 font-semibold">({recentHires.length})</span>
            </div>
            <div className="bg-white border border-gray-100 rounded-3xl p-4 space-y-2 min-h-[220px]">
              {recentHires.length > 0 ? recentHires.map(({ user, daysAgo }) => (
                <div key={user.id} className="flex items-center gap-3 p-2 rounded-2xl hover:bg-gray-50 transition">
                  <Avatar u={user} size={10} />
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-gray-800 text-sm truncate">{user.displayName || user.email?.split('@')[0]}</div>
                    <div className="text-xs text-gray-500">{user.email}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs font-bold text-emerald-600">hace {daysAgo} días</div>
                    {isAdmin && <button onClick={() => handleViewAs(user.id)} className="text-[10px] mt-0.5 px-2 py-0.5 bg-gray-900 text-white rounded-xl">Ver</button>}
                  </div>
                </div>
              )) : (
                <div className="py-8 text-center text-gray-400 text-sm">No hay ingresos recientes en los últimos 60 días.</div>
              )}
            </div>
          </div>

          {/* Equipo en jornada (live from attendance) */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <FiClock className="text-emerald-600" size={18} />
              <span className="font-extrabold text-xl text-gray-900">Equipo en jornada ahora</span>
              <span className="text-sm text-emerald-600 font-semibold">({asesoresWorking})</span>
            </div>
            <div className="bg-white border border-gray-100 rounded-3xl p-4 min-h-[220px]">
              {asesoresWorking > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {attendanceRecords
                    .filter(r => r.date === todayStr && r.entryTime && !r.exitTime)
                    .slice(0, 9)
                    .map((rec, i) => {
                      const u = users.find(x => x.id === rec.userId) || {};
                      return (
                        <div key={i} className="flex items-center gap-2 bg-gray-50 rounded-2xl p-2">
                          <Avatar u={u} size={9} />
                          <div className="min-w-0">
                            <div className="text-sm font-semibold truncate">{u.displayName || rec.userDisplayName || 'Colaborador'}</div>
                            <div className="text-[10px] text-gray-500">Desde {rec.entryTime}</div>
                          </div>
                        </div>
                      );
                    })}
                </div>
              ) : (
                <div className="py-8 text-center text-gray-400 text-sm">Nadie ha marcado entrada todavía o no hay datos de asistencia.</div>
              )}
              <button onClick={onOpenControl} className="mt-3 text-xs w-full text-center py-1.5 rounded-2xl border border-gray-200 hover:bg-gray-50 font-semibold text-gray-600">Ver control de personal completo →</button>
            </div>
          </div>
        </div>

        {/* === SOLICITUDES + DÍAS FESTIVOS + ACCIONES RÁPIDAS (3-column professional row) === */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {/* Solicitudes de tiempo */}
          <div className="bg-white border border-gray-100 rounded-3xl p-5">
            <div className="flex items-center gap-2 mb-3">
              <FiFileText className="text-violet-600" size={17} />
              <span className="font-extrabold text-lg">Solicitudes de tiempo</span>
              <span className="ml-auto text-xs px-2 py-0.5 bg-violet-100 text-violet-700 rounded-full font-bold">4</span>
            </div>
            <div className="text-sm text-gray-600 space-y-1 mb-4">
              <div>• 2 solicitudes de vacaciones pendientes</div>
              <div>• 1 olvido de marcación</div>
              <div>• 1 permiso especial</div>
            </div>
            <button onClick={onOpenControl} className="w-full py-2 rounded-2xl bg-violet-600 hover:bg-violet-700 text-white text-sm font-bold">Ver todas las solicitudes →</button>
          </div>

          {/* Días festivos */}
          <div className="bg-white border border-gray-100 rounded-3xl p-5">
            <div className="flex items-center gap-2 mb-3">
              <FiStar className="text-orange-500" size={17} />
              <span className="font-extrabold text-lg">Días festivos próximos</span>
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span>29 de junio</span> <span className="text-gray-500">San Pedro y San Pablo</span></div>
              <div className="flex justify-between"><span>28 de julio</span> <span className="text-gray-500">Fiestas Patrias</span></div>
              <div className="flex justify-between"><span>30 de agosto</span> <span className="text-gray-500">Santa Rosa de Lima</span></div>
              <div className="flex justify-between"><span>8 de octubre</span> <span className="text-gray-500">Combate de Angamos</span></div>
            </div>
            <div className="mt-3 text-[11px] text-gray-400">Calendario laboral completo en la sección Tiempo (próximamente)</div>
          </div>

          {/* Acciones rápidas */}
          <div className="bg-white border border-gray-100 rounded-3xl p-5">
            <div className="font-extrabold text-lg mb-3 flex items-center gap-2"><FiBriefcase size={17} className="text-sky-600" /> Acciones rápidas</div>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <button onClick={onOpenDocs} className="p-3 bg-gray-50 hover:bg-sky-50 rounded-2xl text-left font-semibold border border-gray-100">Completar documentación</button>
              <button onClick={onOpenControl} className="p-3 bg-gray-50 hover:bg-sky-50 rounded-2xl text-left font-semibold border border-gray-100">Control de Personal</button>
              <button onClick={() => toast('Próximamente: registrar marcación manual')} className="p-3 bg-gray-50 hover:bg-sky-50 rounded-2xl text-left font-semibold border border-gray-100">Marcación manual (admin)</button>
              <button onClick={() => toast('Próximamente: invitar nuevo colaborador')} className="p-3 bg-gray-50 hover:bg-sky-50 rounded-2xl text-left font-semibold border border-gray-100">Invitar colaborador</button>
              <button onClick={() => { if (onOpenSettings) onOpenSettings(); else toast('Configuración en el menú lateral'); }} className="p-3 bg-gray-50 hover:bg-sky-50 rounded-2xl text-left font-semibold border border-gray-100">Configurar horarios y tarifas</button>
              <button onClick={() => toast('Próximamente: clima laboral y encuestas')} className="p-3 bg-gray-50 hover:bg-sky-50 rounded-2xl text-left font-semibold border border-gray-100">Clima del equipo</button>
            </div>
          </div>
        </div>

        {/* === DIRECTORIO DE PERSONAS (wider searchable list) === */}
        <div className="mb-2 mt-2">
          <div className="flex items-center justify-between mb-3">
            <span className="uppercase tracking-[1px] text-xs font-extrabold text-gray-500">Directorio rápido</span>
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Buscar por nombre o email..."
              className="w-80 text-sm border border-gray-200 bg-white rounded-2xl px-4 py-1.5 focus:outline-none focus:ring-2 focus:ring-sky-300"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-3">
            {filteredAsesores.length > 0 ? filteredAsesores.map(u => {
              const hasBirthday = !!getBirthDate(u);
              const hired = getHireDate(u);
              return (
                <div key={u.id} className="flex items-center gap-3 bg-white border border-gray-100 rounded-2xl p-3 hover:border-sky-200 transition group">
                  <Avatar u={u} size={11} />
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-gray-800 truncate text-sm">{u.displayName || u.email?.split('@')[0]}</div>
                    <div className="text-[11px] text-gray-500 truncate flex items-center gap-2">
                      {hasBirthday ? <span className="text-emerald-600">✓ Cumpleaños</span> : 'Sin fecha nacimiento'}
                      {hired && <span className="text-violet-600">• Desde {format(hired, 'MMM yyyy', { locale: es })}</span>}
                    </div>
                  </div>
                  {isAdmin && (
                    <button onClick={() => handleViewAs(u.id)} className="text-[11px] font-bold px-3 py-1 bg-gray-900 text-white rounded-xl group-hover:bg-black">Ver panel</button>
                  )}
                </div>
              );
            }) : (
              <div className="col-span-full text-center py-6 text-gray-400 text-sm">No hay personas que coincidan con la búsqueda.</div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="mt-10 text-center text-xs text-gray-400">
          Tip: Esta página de Inicio se actualiza en tiempo real con las marcaciones y los datos de los usuarios. ¿Quieres agregar widgets de clima laboral, reconocimientos o integración con el calendario?
        </div>
      </div>
    </div>
  );
};

export default RRHHHome;
